import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../api/client';
import Card from '../components/Card';
import Button from '../components/Button';
import FormField, { Input } from '../components/FormField';
import Badge from '../components/Badge';
import { AvatarWithFallback } from '../components/Avatar';
import steamLogo from '../steam-logo.png';
import discordLogo from '../discord-logo.png';

const API_URL = process.env.REACT_APP_API_URL || '';
const MAX_BYTES = 3 * 1024 * 1024;
const CROP_SIZE = 288;   // display circle diameter (px)
const OUTPUT_SIZE = 400; // exported JPEG size (px)

function steamProfileUrl(steamId) {
  if (!steamId) return null;
  return /^\d+$/.test(steamId)
    ? `https://steamcommunity.com/profiles/${steamId}`
    : `https://steamcommunity.com/id/${steamId}`;
}

function clamp(x, y, imgW, imgH, scale) {
  return {
    x: Math.min(0, Math.max(CROP_SIZE - imgW * scale, x)),
    y: Math.min(0, Math.max(CROP_SIZE - imgH * scale, y)),
  };
}

function AvatarCropModal({ file, onConfirm, onCancel }) {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [imgEl, setImgEl] = useState(null);
  const [view, setView] = useState({ scale: 1, x: 0, y: 0 });
  const [minScale, setMinScale] = useState(1);
  const dragRef = useRef(null);
  const viewRef = useRef(view);

  // Keep viewRef always current so pointer handlers read latest state
  useEffect(() => { viewRef.current = view; }, [view]);

  useEffect(() => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const ms = Math.max(CROP_SIZE / img.naturalWidth, CROP_SIZE / img.naturalHeight);
      const ix = (CROP_SIZE - img.naturalWidth * ms) / 2;
      const iy = (CROP_SIZE - img.naturalHeight * ms) / 2;
      setImgEl(img);
      setMinScale(ms);
      setView({ scale: ms, x: ix, y: iy });
    };
    img.src = url;
    return () => URL.revokeObjectURL(url);
  }, [file]);

  // Draw canvas whenever view changes
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !imgEl) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, CROP_SIZE, CROP_SIZE);
    ctx.drawImage(imgEl, view.x, view.y, imgEl.naturalWidth * view.scale, imgEl.naturalHeight * view.scale);
  }, [imgEl, view]);

  // Attach wheel listener as non-passive so we can preventDefault
  useEffect(() => {
    const el = containerRef.current;
    if (!el || !imgEl) return;
    const onWheel = (e) => {
      e.preventDefault();
      const factor = e.deltaY < 0 ? 1.09 : 0.92;
      setView(v => {
        const newScale = Math.max(minScale, Math.min(minScale * 5, v.scale * factor));
        const ratio = newScale / v.scale;
        const { x, y } = clamp(
          CROP_SIZE / 2 - (CROP_SIZE / 2 - v.x) * ratio,
          CROP_SIZE / 2 - (CROP_SIZE / 2 - v.y) * ratio,
          imgEl.naturalWidth, imgEl.naturalHeight, newScale,
        );
        return { scale: newScale, x, y };
      });
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [imgEl, minScale]);

  const handlePointerDown = useCallback((e) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    const { x, y } = viewRef.current;
    dragRef.current = { sx: e.clientX, sy: e.clientY, ox: x, oy: y };
  }, []);

  const handlePointerMove = useCallback((e) => {
    if (!dragRef.current || !imgEl) return;
    setView(v => ({
      ...v,
      ...clamp(
        dragRef.current.ox + (e.clientX - dragRef.current.sx),
        dragRef.current.oy + (e.clientY - dragRef.current.sy),
        imgEl.naturalWidth, imgEl.naturalHeight, v.scale,
      ),
    }));
  }, [imgEl]);

  const handlePointerUp = useCallback(() => { dragRef.current = null; }, []);

  const handleZoom = useCallback((e) => {
    const newScale = Number(e.target.value);
    if (!imgEl) return;
    setView(v => {
      const ratio = newScale / v.scale;
      const { x, y } = clamp(
        CROP_SIZE / 2 - (CROP_SIZE / 2 - v.x) * ratio,
        CROP_SIZE / 2 - (CROP_SIZE / 2 - v.y) * ratio,
        imgEl.naturalWidth, imgEl.naturalHeight, newScale,
      );
      return { scale: newScale, x, y };
    });
  }, [imgEl]);

  const handleConfirm = useCallback(() => {
    if (!imgEl) return;
    const v = viewRef.current;
    const ratio = OUTPUT_SIZE / CROP_SIZE;
    const out = document.createElement('canvas');
    out.width = OUTPUT_SIZE;
    out.height = OUTPUT_SIZE;
    const ctx = out.getContext('2d');
    ctx.drawImage(
      imgEl,
      v.x * ratio, v.y * ratio,
      imgEl.naturalWidth * v.scale * ratio,
      imgEl.naturalHeight * v.scale * ratio,
    );
    out.toBlob(blob => onConfirm(blob), 'image/jpeg', 0.93);
  }, [imgEl, onConfirm]);

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.72)', backdropFilter: 'blur(4px)' }}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
        {/* Header */}
        <div className="px-6 pt-6 pb-4">
          <h3 className="text-lg font-bold text-ocean-900">Bild positionieren</h3>
          <p className="text-sm text-slate-500 mt-0.5">
            Verschiebe das Bild per Drag · zoome mit dem Regler oder Mausrad
          </p>
        </div>

        {/* Crop area */}
        <div className="flex flex-col items-center px-6 pb-2">
          <div
            ref={containerRef}
            className="relative rounded-full overflow-hidden border-4 border-ocean-400 shadow-xl select-none"
            style={{
              width: CROP_SIZE,
              height: CROP_SIZE,
              cursor: dragRef.current ? 'grabbing' : 'grab',
              touchAction: 'none',
            }}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
          >
            <canvas
              ref={canvasRef}
              width={CROP_SIZE}
              height={CROP_SIZE}
              className="block"
            />
            {/* Center crosshair hint */}
            <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
              <div className="w-5 h-px bg-white/30" />
              <div className="absolute w-px h-5 bg-white/30" />
            </div>
            {/* Vignette edge */}
            <div className="absolute inset-0 pointer-events-none rounded-full"
              style={{ boxShadow: 'inset 0 0 24px rgba(0,0,0,0.18)' }} />
          </div>

          {/* Zoom slider */}
          <div className="flex items-center gap-3 mt-5 w-full">
            <svg className="w-4 h-4 text-slate-400 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <circle cx="11" cy="11" r="7"/><path d="m21 21-3.5-3.5M8 11h6"/>
            </svg>
            <input
              type="range"
              min={minScale}
              max={minScale * 5}
              step={minScale * 0.005}
              value={view.scale}
              onChange={handleZoom}
              className="flex-1 h-2 appearance-none rounded-full outline-none"
              style={{ accentColor: '#0284c7' }}
            />
            <svg className="w-5 h-5 text-slate-400 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <circle cx="11" cy="11" r="7"/><path d="m21 21-3.5-3.5M8 11h6M11 8v6"/>
            </svg>
          </div>
          <p className="text-xs text-slate-400 mt-2 mb-4">
            {Math.round((view.scale / minScale) * 100)}% Zoom
          </p>
        </div>

        {/* Footer buttons */}
        <div className="flex gap-3 px-6 pb-6">
          <button
            onClick={onCancel}
            className="flex-1 text-sm font-semibold text-slate-600 border border-slate-200 bg-white hover:bg-slate-50 px-4 py-2.5 rounded-xl transition-colors"
          >
            Abbrechen
          </button>
          <button
            onClick={handleConfirm}
            disabled={!imgEl}
            className="flex-1 text-sm font-semibold text-white bg-ocean-600 hover:bg-ocean-700 disabled:opacity-50 px-4 py-2.5 rounded-xl transition-colors"
          >
            Übernehmen
          </button>
        </div>
      </div>
    </div>
  );
}

function AvatarSection({ user, onUpdate }) {
  const fileRef = useRef(null);
  const [pendingFile, setPendingFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setError(''); setSuccess('');

    const allowed = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowed.includes(file.type)) {
      setError('Ungültiges Dateiformat. Erlaubt: JPEG, PNG, GIF, WebP');
      e.target.value = '';
      return;
    }
    if (file.size > MAX_BYTES) {
      setError(`Bild zu groß (${(file.size / 1024 / 1024).toFixed(1)} MB). Maximal 3 MB erlaubt.`);
      e.target.value = '';
      return;
    }
    setPendingFile(file);
    e.target.value = '';
  };

  const handleCropConfirm = async (blob) => {
    setPendingFile(null);
    setUploading(true); setError(''); setSuccess('');
    try {
      const form = new FormData();
      form.append('file', new File([blob], 'avatar.jpg', { type: 'image/jpeg' }));
      const res = await api.post('/api/auth/me/avatar', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onUpdate(res.data);
      setSuccess('Profilbild erfolgreich gespeichert.');
    } catch (err) {
      setError(err.response?.data?.detail || 'Fehler beim Hochladen');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Profilbild wirklich löschen?')) return;
    setDeleting(true); setError(''); setSuccess('');
    try {
      const res = await api.delete('/api/auth/me/avatar');
      onUpdate(res.data);
      setSuccess('Profilbild gelöscht.');
    } catch (err) {
      setError(err.response?.data?.detail || 'Fehler beim Löschen');
    } finally {
      setDeleting(false);
    }
  };

  const avatarSrc = user?.avatar_url ? `${API_URL}${user.avatar_url}` : null;

  return (
    <>
      {pendingFile && (
        <AvatarCropModal
          file={pendingFile}
          onConfirm={handleCropConfirm}
          onCancel={() => setPendingFile(null)}
        />
      )}

      <Card className="mb-6">
        <h2 className="section-title">Profilbild</h2>
        {success && <div className="alert-success mb-4">{success}</div>}
        {error   && <div className="alert-error   mb-4">{error}</div>}
        <div className="flex items-center gap-5 flex-wrap">
          {/* Preview */}
          <div className="relative flex-shrink-0">
            {avatarSrc ? (
              <img
                src={avatarSrc}
                alt="Profilbild"
                className="w-24 h-24 rounded-2xl object-cover border-2 border-ocean-200 shadow"
              />
            ) : (
              <div className="w-24 h-24 rounded-2xl bg-ocean-100 flex items-center justify-center text-3xl font-bold text-ocean-500 border-2 border-dashed border-ocean-200">
                {user?.firstname?.[0]}{user?.lastname?.[0]}
              </div>
            )}
            {uploading && (
              <div className="absolute inset-0 rounded-2xl bg-white/70 flex items-center justify-center">
                <div className="animate-spin rounded-full h-7 w-7 border-2 border-ocean-500 border-t-transparent" />
              </div>
            )}
          </div>

          {/* Buttons */}
          <div className="flex flex-col gap-2">
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              className="hidden"
              onChange={handleFile}
            />
            <Button size="sm" onClick={() => fileRef.current?.click()} loading={uploading} disabled={deleting}>
              {user?.avatar_url ? 'Bild ändern' : 'Bild hochladen'}
            </Button>
            {user?.avatar_url && (
              <Button size="sm" variant="danger" onClick={handleDelete} loading={deleting} disabled={uploading}>
                Bild löschen
              </Button>
            )}
            <p className="text-xs text-slate-400 mt-0.5">JPEG, PNG, GIF oder WebP · max. 3 MB</p>
          </div>
        </div>
      </Card>
    </>
  );
}

export default function Profile() {
  const { user, updateUser } = useAuth();
  const [form, setForm] = useState({
    username:     user?.username     || '',
    firstname:    user?.firstname    || '',
    lastname:     user?.lastname     || '',
    nickname:     user?.nickname     || '',
    discord_name: user?.discord_name || '',
    steam_id:     user?.steam_id     || '',
    email:        user?.email        || '',
  });
  const [pwForm, setPwForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [saving, setSaving]     = useState(false);
  const [pwSaving, setPwSaving] = useState(false);
  const [success, setSuccess]   = useState('');
  const [error, setError]       = useState('');
  const [pwError, setPwError]   = useState('');
  const [pwSuccess, setPwSuccess] = useState('');
  const [seats, setSeats]       = useState([]);

  useEffect(() => {
    if (!user?.id) return;
    api.get(`/api/users/${user.id}/profile`).then((r) => setSeats(r.data.seats || [])).catch(() => {});
  }, [user?.id]);

  const set  = (f) => (e) => setForm({ ...form, [f]: e.target.value });
  const setPw = (f) => (e) => setPwForm({ ...pwForm, [f]: e.target.value });

  const saveProfile = async (e) => {
    e.preventDefault();
    setSaving(true); setError(''); setSuccess('');
    try {
      const res = await api.put('/api/auth/me', form);
      updateUser(res.data);
      setSuccess('Profil erfolgreich gespeichert.');
    } catch (err) {
      setError(err.response?.data?.detail || 'Fehler beim Speichern');
    } finally { setSaving(false); }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    setPwError(''); setPwSuccess('');
    if (pwForm.new_password !== pwForm.confirm_password) {
      setPwError('Die neuen Passwörter stimmen nicht überein.'); return;
    }
    if (pwForm.new_password.length < 8) {
      setPwError('Passwort muss mindestens 8 Zeichen haben.'); return;
    }
    setPwSaving(true);
    try {
      await api.post('/api/auth/change-password', {
        current_password: pwForm.current_password,
        new_password: pwForm.new_password,
      });
      setPwSuccess('Passwort erfolgreich geändert.');
      setPwForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      setPwError(err.response?.data?.detail || 'Fehler beim Ändern des Passworts');
    } finally { setPwSaving(false); }
  };

  return (
    <div className="max-w-xl mx-auto">
      <h1 className="page-title">Mein Profil</h1>

      {/* Info-Banner */}
      <Card className="mb-6 flex items-center gap-4">
        <AvatarWithFallback user={user} size="lg" />
        <div className="flex-1 min-w-0">
          <p className="font-bold text-ocean-900 text-lg">{user?.firstname} {user?.lastname}</p>
          <p className="text-sm text-slate-500">@{user?.username}</p>
          {user?.nickname && <p className="text-xs text-slate-400">Nickname: {user.nickname}</p>}
          <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
            {user?.steam_id && (
              <a href={steamProfileUrl(user.steam_id)} target="_blank" rel="noopener noreferrer"
                className="text-xs text-slate-600 hover:text-ocean-600 hover:underline inline-flex items-center gap-1">
                <img src={steamLogo} alt="Steam" className="w-3.5 h-3.5 object-contain" />
                Steam-Profil
              </a>
            )}
            {user?.discord_name && (
              <span className="text-xs text-slate-600 inline-flex items-center gap-1">
                <img src={discordLogo} alt="Discord" className="w-3.5 h-3.5 object-contain" />
                {user.discord_name}
              </span>
            )}
          </div>
        </div>
        <Badge status={user?.role} />
      </Card>

      {/* Avatar Upload */}
      <AvatarSection user={user} onUpdate={updateUser} />

      {/* Sitzplätze */}
      {seats.length > 0 && (
        <Card className="mb-6">
          <h2 className="section-title">Mein Sitzplatz</h2>
          <div className="space-y-2">
            {seats.map((s) => (
              <div key={s.event_id} className="flex justify-between items-center text-sm border-b border-slate-50 pb-2 last:border-0 last:pb-0">
                <Link to={`/events/${s.event_id}`} className="text-ocean-700 hover:underline font-medium">{s.event_name}</Link>
                <span className="font-semibold text-slate-700">🪑 {s.seat_label}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Profil bearbeiten */}
      <Card className="mb-6">
        <h2 className="section-title">Persönliche Daten</h2>
        {success && <div className="alert-success mb-4">{success}</div>}
        {error   && <div className="alert-error   mb-4">{error}</div>}
        <form onSubmit={saveProfile}>
          <FormField label="Benutzername" required>
            <Input value={form.username} onChange={set('username')} required minLength={3} maxLength={30} />
            <p className="text-xs text-slate-400 mt-1">3-30 Zeichen, nur Buchstaben, Zahlen, '_', '-' und '.'</p>
          </FormField>
          <div className="grid grid-cols-2 gap-x-4">
            <FormField label="Vorname" required>
              <Input value={form.firstname} onChange={set('firstname')} required />
            </FormField>
            <FormField label="Nachname" required>
              <Input value={form.lastname} onChange={set('lastname')} required />
            </FormField>
          </div>
          <FormField label="E-Mail" required>
            <Input type="email" value={form.email} onChange={set('email')} required />
          </FormField>
          <FormField label="Nickname (optional)">
            <Input value={form.nickname} onChange={set('nickname')} placeholder="GameKing42" />
          </FormField>
          <FormField label="Discord (optional)">
            <Input value={form.discord_name} onChange={set('discord_name')} placeholder="username#1234" />
          </FormField>
          <FormField label="Steam-ID (optional)">
            <Input value={form.steam_id} onChange={set('steam_id')} placeholder="z.B. dein_steamname oder Profil-Link" />
            <p className="text-xs text-slate-400 mt-1">Steam-Profilname, SteamID64 oder kompletter Profil-Link</p>
          </FormField>
          <Button type="submit" loading={saving}>Änderungen speichern</Button>
        </form>
      </Card>

      {/* Passwort ändern */}
      <Card>
        <h2 className="section-title">Passwort ändern</h2>
        {pwSuccess && <div className="alert-success mb-4">{pwSuccess}</div>}
        {pwError   && <div className="alert-error   mb-4">{pwError}</div>}
        <form onSubmit={changePassword}>
          <FormField label="Aktuelles Passwort" required>
            <Input type="password" value={pwForm.current_password} onChange={setPw('current_password')}
              autoComplete="current-password" required />
          </FormField>
          <FormField label="Neues Passwort" required>
            <Input type="password" value={pwForm.new_password} onChange={setPw('new_password')}
              autoComplete="new-password" required />
            <p className="text-xs text-slate-400 mt-1">Min. 8 Zeichen, Groß-/Kleinbuchstaben und eine Zahl</p>
          </FormField>
          <FormField label="Neues Passwort bestätigen" required>
            <Input type="password" value={pwForm.confirm_password} onChange={setPw('confirm_password')}
              autoComplete="new-password" required />
          </FormField>
          <Button type="submit" loading={pwSaving} variant="secondary">Passwort ändern</Button>
        </form>
      </Card>
    </div>
  );
}
